import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getCurrentToken } from 'src/utils';
import { useQueryProxy } from 'src/operations/proxy/user';
import * as queries from 'src/operations/queries';
import { useLazyQuery, useApolloClient, useQuery, useMutation } from '@apollo/client';
import { openNotification } from 'src/components/common/Notification';
import { isLogedIn, userInfoVar } from 'src/apollo/cache';
import { GET_LEXILE_SCORE } from 'src/operations/queries/consult';
import { ENGLISHTAP_BOOK_CREDIT_CHECK_MULTI, GET_BOOK_CREDIT_LIST } from 'src/operations/queries/bookCredit';
import { getEpubocAssignmentPeriod } from './studyCreditDates';
import { ASSIGN_BOOK_TO_STUDENTS } from 'src/operations/mutations/bookCredit';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { classStoreData } from 'src/operations/store';
import fscreen from 'fscreen';
import * as axios from 'axios';

export const useForm = (callback, initialState = {}) => {
  const [values, setValues] = useState(initialState);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setValues({
        ...values,
        [name]: checked,
      });
      return;
    }

    setValues({
      ...values,
      [name]: value,
    });
  };

  const onSelectChange = (value, select) => {
    setValues({ ...values, [select.name]: select.value });
  };

  const onSubmit = () => {
    callback();
  };

  return {
    values,
    onChange,
    onSelectChange,
    onSubmit,
  };
};

export const useModal = () => {
  const [visible, setVisible] = useState(false);
  const [popupMode, setPopupMode] = useState('');

  const showModal = (mode = 'create') => {
    setPopupMode(mode);
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  return {
    visible,
    showModal,
    handleCancel,
    popupMode,
  };
};

export const storage = {
  getItem: (keyword) => {
    return window.localStorage.getItem(keyword);
  },

  getItemArray: (array) => {
    return array.length ? array.split(',') : array;
  },

  setItem: (keyword, value) => {
    window.localStorage.setItem(keyword, value);
  },
  removeItem: (keyword) => {
    window.localStorage.removeItem(keyword);
  },
};

export const useInput = (initialValue = null) => {
  const [value, setValue] = useState(initialValue);
  const handler = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  const reset = useCallback(() => setValue(initialValue), [initialValue]);

  return [value, handler, reset];
};

export const useSelect = (key, initialValue = null) => {
  const [value, setValue] = useState(initialValue);
  const handler = useCallback((key) => {
    setValue(key);
  }, []);

  return [value, handler];
};

//아임포트 연동 hook 구현
const loadScript = (script_url, script_id) => {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.id = script_id;
      script.src = script_url;
      script.onload = () => resolve();
      document.head.appendChild(script);
    } catch (e) {
      reject(e);
    }
  });
};

const JQUERY_URL = `https://code.jquery.com/jquery-1.12.4.min.js`;
const IAMPORT_URL = `https://cdn.iamport.kr/js/iamport.payment-1.1.5.js`;

export const useIamport = (iamportCode) => {
  const iamportInstance = useRef({});
  const [loaded, setLoaded] = useState(false);
  const requestPay = (params, onSuccess, onFail) => {
    iamportInstance.current.request_pay(params, (resp) => {
      if (resp.success) {
        onSuccess && onSuccess(resp);
      } else {
        onFail && onFail(resp);
      }
    });
  };

  useEffect(() => {
    (async () => {
      await loadScript(JQUERY_URL, 'react-use-iamport-jquery');
      await loadScript(IAMPORT_URL, 'react-use-iamport-iamport');
      const { IMP } = window;
      iamportInstance.current = IMP;
      IMP.init(iamportCode);
      setLoaded(true);
    })();
  }, [iamportCode]);
  return { loaded, requestPay };
};

const NOT_ACCEPTED_PATH = ['/teachers', '/students', '/fcManager', '/learning'];
export const useAuthCheck = () => {
  const location = useLocation();
  const token = getCurrentToken();
  const { data } = useQueryProxy(queries.getUser.USER_ME, {
    variables: { token },
    skip: !token,
  });
  const userType = useMemo(() => data?.me?.type, [data]);
  const isAccepted = useMemo(() => {
    if (userType !== undefined) {
      if (userType === 0) {
        const find = NOT_ACCEPTED_PATH.find((item) => {
          return location.pathname.indexOf(item) !== -1;
        });
        if (find) {
          return false;
        }
      }
    }
    return true;
  }, [userType, location]);

  //console.log('location.pathname', location.pathname);

  return { isAccepted };
};

export const useLoginTo = (isReturn) => {
  const client = useApolloClient();
  const history = useHistory();
  const [loginUser, { data: dataLogin }] = useLazyQuery(queries.getUser.LOGIN_USER);
  const loginTo = async (id) => {
    if (!isReturn && userInfoVar()?.id) {
      window.localStorage.setItem('culp_return_id', userInfoVar().id);
    }
    isLogedIn(false);
    userInfoVar(null);
    await client.clearStore();
    client.cache.gc();
    window.localStorage.removeItem('culp_token');
    loginUser({ variables: { id, pw: '1', ingnore_pw: 'yes' } });
  };

  useEffect(() => {
    if (dataLogin?.login) {
      window.localStorage.setItem('culp_token', JSON.stringify(dataLogin.login));
      if (isReturn) {
        window.localStorage.removeItem('culp_return_id');
      }
      if (window.Android && window.Android.SaveUserToken) {
        window.Android.SaveUserToken(JSON.stringify(dataLogin.login));
      }
      isLogedIn(!!dataLogin?.login);
      window.location.href = '/';
    }
    // eslint-disable-next-line
  }, [dataLogin]);
  return loginTo;
};

export const useGetLexileScoreFromApi = (formId, numberCorrect) => {
  const {
    data: getLexileScore,
    loading,
    refetch,
  } = useQuery(GET_LEXILE_SCORE, {
    fetchPolicy: 'no-cache',
    variables: { formId, numberCorrect },
  });
  return getLexileScore;
};

export const useFullscreenStatus = (elRef) => {
  const [isFullscreen, setIsFullscreen] = useState(document[getBrowserFullscreenElementProp()] != null);
  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(document[getBrowserFullscreenElementProp()] != null);
  }, []);

  const setFullscreen = (val) => {
    if (elRef.current == null) return;

    if (val === false) {
      try {
        const exitResult = fscreen.exitFullscreen();
        if (exitResult && typeof exitResult.catch === 'function') {
          exitResult.catch((e) => {
            console.log(e);
            setIsFullscreen(false);
          });
        }
        setIsFullscreen(false);
      } catch (e) {
        console.log(e);
        setIsFullscreen(false);
      }
    } else {
      if (fscreen.fullscreenEnabled && !isFullscreen) {
        const hasTransientActivation =
          typeof navigator === 'undefined' ||
          !navigator.userActivation ||
          navigator.userActivation.isActive ||
          navigator.userActivation.hasBeenActive;

        if (!hasTransientActivation) {
          setIsFullscreen(false);
          return;
        }

        try {
          const requestResult = fscreen.requestFullscreen(elRef.current);
          if (requestResult && typeof requestResult.catch === 'function') {
            requestResult.catch((e) => {
              console.log(e);
              setIsFullscreen(false);
            });
          }
        } catch (e) {
          console.log(e);
          setIsFullscreen(false);
        }
      }
    }
  };

  useLayoutEffect(() => {
    fscreen.addEventListener('fullscreenchange', handleFullscreenChange);
    document.onfullscreenchange = handleFullscreenChange;

    return () => {
      fscreen.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.onfullscreenchange = undefined;
    };
  }, [handleFullscreenChange]);

  return [isFullscreen, setFullscreen];
};

function getBrowserFullscreenElementProp() {
  if (typeof document.fullscreenElement !== 'undefined') {
    return 'fullscreenElement';
  } else if (typeof document.mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement';
  } else if (typeof document.msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement';
  } else if (typeof document.webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement';
  } else {
    throw new Error('fullscreenElement is not supported by this browser');
  }
}

export const useTextToSpeach = (speed, voice) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [texts, setTexts] = useState([]);
  const audio = useRef(new Audio());
  const token = getCurrentToken();

  const processTTS = useCallback(
    async (text, MAX) => {
      try {
        const res = await axios('https://p333gnhytx2eeyncgsxpv3kmia0hfnmt.lambda-url.ap-northeast-2.on.aws/', {
          method: 'POST',
          data: {
            text,
            action: 'tts',
            speed: speed,
            voice: voice,
          },
          //speed,voice
          headers: {
            'Content-Type': 'application/json',
            'x-key': token,
          },
          timeout: 10000, // Set a timeout of 10 seconds
        });
        if (!res.data) {
          throw new Error('Invalid response from TTS service');
        }
        return res.data;
      } catch (error) {
        console.error('Error processing TTS:', error, MAX);
        if (MAX < 3) {
          return processTTS(text, MAX + 1); // Retry up to 3 times
        } else {
          throw new Error('Failed to process TTS after multiple attempts');
        }
      }
    },
    [speed, voice],
  );

  const speak = async (text, cb, audioPaths = []) => {
    // split id .mp3 exist in text

    /// convert from markdown to text
    text = text.startsWith('markdown') ? text.split('markdown')[1] : text;

    //regex replace all images
    text = text.replace(/!\[[^\]]*\]\((.*?)\)/g, '');
    if (text.length === 0) {
      cb();
      return;
    }
    setIsSpeaking(true);
    const audioRegex = /\[[^\]]*\]\((.*?)\)/g;
    let splitText = text.split(audioRegex);

    let textArray = [];
    splitText.forEach((text) => {
      if (text !== '' && text !== undefined) {
        if ((text.endsWith('.mp3') || text.endsWith('.mp4')) && !text.includes('http')) {
          let findIndx = audioPaths.findIndex((path) => path.endsWith(text));
          if (findIndx !== -1) {
            text = audioPaths[findIndx];
          } else {
            //check if prefix with audio_ then remove
            if (text.startsWith('audio_')) {
              text = text.replace('audio_', '');
            }
            findIndx = audioPaths.findIndex((path) => path.endsWith(text));
            if (findIndx !== -1) {
              text = audioPaths[findIndx];
            }
          }
        }

        textArray.push(text);
      }
    });

    setTexts(textArray);

    if (cb) {
      cb();
    }
  };

  useEffect(() => {
    if (texts.length > 0) {
      playAudio(0);
    }
  }, [texts]);

  useEffect(() => {
    if (isSpeaking === false) {
      audio.current.pause();
    }
  }, [isSpeaking]);

  const playAudio = async (i) => {
    if (texts.length === 0) return;
    if (texts[i].startsWith('http')) {
      if (texts[i].includes('.mp3')) {
        audio.current.src = texts[i];
      }
    } else {
      let curText = texts[i];
      if (curText.length > 4000) {
        let firstPart = curText.slice(0, 3500);
        let leftPart = curText.slice(3500);
        let midstartPart = leftPart.length - 500;
        let restPart = leftPart.slice(midstartPart);
        curText = `${firstPart} ${restPart}`;
      }
      let res = await processTTS(curText, 0);
      audio.current.src = res.url;
    }
    audio.current.play();
    audio.current.onended = () => {
      if (texts.length > 1) {
        setTexts(texts.slice(1));
      } else {
        setIsSpeaking(false);
      }
    };
  };

  const stop = () => {
    if (audio.current.src !== '') audio.current.pause();
    setTexts([]);
    setIsSpeaking(false);
  };

  return { isSpeaking, texts, speak, stop };
};

export const useTextToSpeach2 = (speed, voice) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [texts, setTexts] = useState([]);
  const audio = useRef(new Audio());
  const [isSpeakingActive, setIsSpeakingActive] = useState(false);
  const textsRef = useRef(texts);
  const token = getCurrentToken();
  const audioUnlockedRef = useRef(false);

  // iOS Safari audio unlock - play silent audio on first user interaction
  useEffect(() => {
    const unlockAudio = () => {
      if (!audioUnlockedRef.current) {
        audio.current.src =
          'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAhgCMEg3VAAAAAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/7EGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=';
        audio.current
          .play()
          .then(() => {
            audio.current.pause();
            audio.current.currentTime = 0;
            audioUnlockedRef.current = true;
          })
          .catch(() => {
            // Silent fail - will retry on next interaction
          });
      }
    };

    // Unlock on any user interaction
    document.addEventListener('touchstart', unlockAudio, { once: true });
    document.addEventListener('click', unlockAudio, { once: true });

    return () => {
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('click', unlockAudio);
    };
  }, []);

  const processTTS = useCallback(
    async (text, MAX) => {
      try {
        const res = await axios('https://p333gnhytx2eeyncgsxpv3kmia0hfnmt.lambda-url.ap-northeast-2.on.aws/', {
          method: 'POST',
          data: {
            text,
            action: 'tts',
            speed: speed,
            voice: voice,
          },
          //speed,voice
          headers: {
            'Content-Type': 'application/json',
            'x-key': token,
          },
          timeout: 10000, // Set a timeout of 10 seconds
        });
        if (!res.data) {
          throw new Error('Invalid response from TTS service');
        }
        return res.data;
      } catch (error) {
        console.error('Error processing TTS:', error, MAX);
        if (MAX < 3) {
          return processTTS(text, MAX + 1); // Retry up to 3 times
        } else {
          throw new Error('Failed to process TTS after multiple attempts');
        }
      }
    },
    [speed, voice],
  );

  const speak = async (text, cb, audioPaths = []) => {
    // split id .mp3 exist in text

    /// convert from markdown to text
    text = text.startsWith('markdown') ? text.split('markdown')[1] : text;

    //regex replace all images
    text = text.replace(/!\[[^\]]*\]\((.*?)\)/g, '');
    if (text.length === 0) {
      cb();
      return;
    }
    setIsSpeaking(true);
    const audioRegex = /\[[^\]]*\]\((.*?)\)/g;
    let splitText = text.split(audioRegex);

    let textArray = [];
    splitText.forEach((text) => {
      if (text !== '' && text !== undefined) {
        if ((text.endsWith('.mp3') || text.endsWith('.mp4')) && !text.includes('http')) {
          let findIndx = audioPaths.findIndex((path) => path.endsWith(text));
          if (findIndx !== -1) {
            text = audioPaths[findIndx];
          } else {
            //check if prefix with audio_ then remove
            if (text.startsWith('audio_')) {
              text = text.replace('audio_', '');
            }
            findIndx = audioPaths.findIndex((path) => path.endsWith(text));
            if (findIndx !== -1) {
              text = audioPaths[findIndx];
            }
          }
        }

        textArray.push(text);
      }
    });

    setTexts([...texts, ...textArray]);
    textsRef.current = [...texts, ...textArray];
    if (isSpeaking === false) {
      playAudio(0);
    }

    if (cb) {
      cb();
    }
  };

  // useEffect(() => {
  //   if (texts.length > 0) {
  //     playAudio(0);
  //   }
  // }, [texts]);

  useEffect(() => {
    if (isSpeaking === false) {
      audio.current.pause();
    }
  }, [isSpeaking]);

  const playAudio = async (i) => {
    if (textsRef.current.length === 0) return;
    if (textsRef.current[i].startsWith('http')) {
      if (textsRef.current[i].includes('.mp3')) {
        audio.current.src = textsRef.current[i];
      }
    } else {
      let curText = textsRef.current[i];
      if (curText.length > 4000) {
        let firstPart = curText.slice(0, 3500);
        let leftPart = curText.slice(3500);
        let midstartPart = leftPart.length - 500;
        let restPart = leftPart.slice(midstartPart);
        curText = `${firstPart} ${restPart}`;
      }
      let res = await processTTS(curText, 0);
      audio.current.src = res.url;
    }
    setIsSpeakingActive(true);

    // iOS Safari requires play() to be called in response to user interaction
    // Handle play promise rejection gracefully
    const playPromise = audio.current.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error('Audio play failed:', error);
        // Reset state if play fails
        setIsSpeaking(false);
        setIsSpeakingActive(false);
        textsRef.current = [];
        setTexts([]);
      });
    }

    audio.current.onended = () => {
      if (textsRef.current.length > 1) {
        textsRef.current = textsRef.current.slice(1);
        playAudio(0);
      } else {
        setTimeout(() => {
          setIsSpeaking(false);
          setIsSpeakingActive(false);
        }, 1000);
      }
    };

    audio.current.onerror = () => {
      console.error('Audio error occurred');
      setIsSpeaking(false);
      setIsSpeakingActive(false);
      textsRef.current = [];
      setTexts([]);
    };
  };

  const stop = () => {
    if (audio.current.src !== '') audio.current.pause();
    setTexts([]);
    setIsSpeaking(false);
    setIsSpeakingActive(false);
  };

  return { isSpeaking, texts, speak, stop, isSpeakingActive };
};

export function useMobileOrientation() {
  const [state, setState] = useState(() => {
    let orientation = window.innerWidth > window.innerHeight ? 90 : 0;
    orientation = window.innerWidth === window.innerHeight ? 90 : orientation;
    return {
      isPortrait: orientation === 0,
      isLandscape: orientation === 90,
      orientation: orientation === 0 ? 'portrait' : 'landscape',
    };
  });
  const handleOrientationChange = useCallback(() => {
    let orientation = window.innerWidth > window.innerHeight ? 90 : 0;
    orientation = window.innerWidth === window.innerHeight ? 90 : orientation;
    const next = {
      isPortrait: orientation === 0,
      isLandscape: orientation === 90,
      orientation: orientation === 0 ? 'portrait' : 'landscape',
    };
    state.orientation !== next.orientation && setState(next);
  }, [state.orientation]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleOrientationChange();
      window.addEventListener('load', handleOrientationChange, false);
      window.addEventListener('resize', handleOrientationChange, false);
    }
    return () => {
      window.removeEventListener('resize', handleOrientationChange, false);
      window.removeEventListener('load', handleOrientationChange, false);
    };
  }, [handleOrientationChange]);
  return state;
}

export const useStudentStudyAccessCheck = () => {
  const creditClient = useApolloClient();
  const assigningCredit = useRef(false);
  const companyName = useSelector(classStoreData);
  const [isOnlineContent, setIsOnlineContent] = useState(true);
  const [isAIspeaking, setIsAIspeaking] = useState(true);
  const [isAiCoach, setIsAiCoach] = useState(true);
  const [isAiWriting, setIsAiWriting] = useState(true);
  const [isLevelUp, setIsLevelUp] = useState(true);
  const { pathname } = useLocation();
  const { data: levelupCreditCheck, loading: checkLoading } = useQuery(ENGLISHTAP_BOOK_CREDIT_CHECK_MULTI, {
    variables: {
      user_idx: parseInt(userInfoVar()?.idx),
      company_idx: parseInt(userInfoVar()?.campus?.company?.idx),
      root_book_codes: companyName === 'epublic' ? ['LEVELUP', 'EPUBOC'] : ['LTR', 'KTP'],
    },
    skip:
      !userInfoVar() ||
      userInfoVar()?.type !== 0 ||
      userInfoVar()?.campus_idx === 3787 ||
      !['english42', 'englishtap', 'epublic'].includes(companyName) ||
      !(pathname.includes('level-up') || pathname === '/' || pathname === '/dashboard'),
  });
  const { data: epublicContentCheck, loading: epubCheckLoading } = useQuery(ENGLISHTAP_BOOK_CREDIT_CHECK_MULTI, {
    variables: {
      user_idx: parseInt(userInfoVar()?.idx),
      company_idx: parseInt(userInfoVar()?.campus?.company?.idx),
      root_book_codes: ['EPUBOC'],
    },
    skip:
      !userInfoVar() ||
      userInfoVar()?.campus_idx === 3787 ||
      userInfoVar()?.type !== 0 ||
      !['epublic'].includes(companyName) ||
      !(pathname === '/' || pathname === '/dashboard'),
  });
  const { data: studentUserData } = useQuery(queries.getUser.GET_STUDENT_USER, {
    skip: !userInfoVar() || userInfoVar()?.type !== 0,
  });
  const [assignBook, { loading: loadingAssignBook }] = useMutation(ASSIGN_BOOK_TO_STUDENTS, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      openNotification('이용권 부여완료!');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError(error) {
      alert(error.message);
      console.log('error', error);
    },
  });
  const handleAssignCreditForEpublic = async (selectedStartDate) => {
    if (loadingAssignBook || assigningCredit.current) return;
    const classIdx = studentUserData?.getUserData?.class_student?.[0]?.class?.idx;
    if (!classIdx) {
      openNotification('No class found for this student. Please contact the campus.');
      return;
    }
    if (companyName === 'epublic') {
      assigningCredit.current = true;
      try {
        const campusIdx = parseInt(userInfoVar()?.campus_idx);
        const { data } = await creditClient.query({
          query: GET_BOOK_CREDIT_LIST,
          variables: { campus_idx: campusIdx },
          fetchPolicy: 'network-only',
        });
        const startDate = typeof selectedStartDate === 'string' || moment.isMoment(selectedStartDate) || selectedStartDate instanceof Date
          ? selectedStartDate : undefined;
        const period = getEpubocAssignmentPeriod(data?.getBookCreditList, startDate);
        await assignBook({
          variables: {
            assign_book_input: {
              campus_idx: campusIdx,
              book_code: 'EPUBOC',
              user_idx: parseInt(userInfoVar()?.idx),
              study_type: 'learn',
              start_date: period.start_date,
              end_date: period.end_date,
              class_idx: classIdx,
              student_class_idx: [classIdx],
              days: period.days,
            },
          },
        }).catch(() => {}); // The mutation's onError displays assignment errors.
      } catch (error) {
        openNotification(error.message);
      } finally {
        assigningCredit.current = false;
      }
    }
  };
  const StudentAccess = useMemo(() => {
    if (userInfoVar()?.campus?.company?.study_access_check) {
      return JSON.parse(userInfoVar()?.campus?.company?.study_access_check);
    }
    return false;
  }, [userInfoVar()?.campus?.company]);

  const userAccessAvailable = useMemo(() => {
    if (userInfoVar()?.study_access_available) {
      return typeof userInfoVar()?.study_access_available === 'string' ? JSON.parse(userInfoVar()?.study_access_available) : [];
    }
    return [];
  }, [userInfoVar()?.study_access_available]);

  useEffect(() => {
    if (StudentAccess) {
      let findOnlineContent = StudentAccess.find((item) => item.id === 'online_content');
      let findAIspeaking = StudentAccess.find((item) => item.id === 'ai_speaking');
      let findAiCoach = StudentAccess.find((item) => item.id === 'ai_coach');
      let findAiWriting = StudentAccess.find((item) => item.id === 'ai_writing');
      let findLevelUp = StudentAccess.find((item) => item.id === 'levelup');

      if (findOnlineContent) {
        setIsOnlineContent(userAccessAvailable.includes(findOnlineContent.id));
      }
      if (findAIspeaking) {
        setIsAIspeaking(userAccessAvailable.includes(findAIspeaking.id));
      }
      if (findAiCoach) {
        setIsAiCoach(userAccessAvailable.includes(findAiCoach.id));
      }
      if (findAiWriting) {
        setIsAiWriting(userAccessAvailable.includes(findAiWriting.id));
      }

      if (findLevelUp) {
        setIsLevelUp(userAccessAvailable.includes(findLevelUp.id));
      }
    }
  }, [StudentAccess, userAccessAvailable]);

  useEffect(() => {
    if (['english42', 'englishtap', 'epublic'].includes(companyName) && userInfoVar()?.type === 0) {
      if (levelupCreditCheck && !checkLoading) {
        if (!levelupCreditCheck.hasValidRootBookCreditMultiple) {
          setIsLevelUp(false);
        }
      }
      if (epublicContentCheck && !epubCheckLoading) {
        if (!epublicContentCheck.hasValidRootBookCreditMultiple) {
          setIsOnlineContent(false);
          setIsAIspeaking(false);
          setIsAiCoach(false);
          setIsAiWriting(false);
        }
      }
    }
  }, [companyName, levelupCreditCheck, checkLoading, epublicContentCheck, epubCheckLoading]);

  return { isOnlineContent, isAIspeaking, isAiCoach, isAiWriting, isLevelUp, handleAssignCreditForEpublic };
};
